/* eslint-disable max-len */
export const smeWorkspaceI18n = {
  smeWorkspace: {
    userTypeReadable: 'Subject Matter Expert',

    mfa: {
      title: 'Verify your identity',
      intro: 'For your security, verify the code we send you before viewing assessment content.',
      sendCode: 'Send code',
      resend: 'Resend code',
      resendIn: 'Resend available in {seconds}s',
      codeSentTo: 'We sent a 6-digit code to {destination}.',
      rememberDevice: 'This device will be remembered for {hours} hours.',
      codeLabel: 'Verification code',
      verify: 'Verify',
      verifying: 'Verifying…',
      sending: 'Sending…',
      errors: {
        codeInvalid: 'That code is incorrect.',
        codeExpired: 'That code has expired. Request a new one.',
        challengeNotFound: 'That code is no longer valid. Request a new one.',
        challengeLimit: 'Too many code requests. Please wait before requesting another.',
        generic: 'Something went wrong. Please try again.',
      },
    },

    nav: {
      dashboard: 'Dashboard',
      myItems: 'My items',
      reviewQueue: 'Review queue',
    },

    accessDenied: {
      title: 'SME access required',
      message: 'This workspace requires an active SME profile with a signed contract and completed '
        + 'training on record. Please contact Premier Food Safety to set up access.',
    },

    error: {
      title: 'Something went wrong',
      generic: 'Something went wrong. Please try again.',
      retry: 'Try again',
    },

    state: {
      loading: 'Loading',
    },

    roleRequired: {
      write: {
        title: 'Item writing not enabled',
        message: 'Your SME profile does not have item-writing access. Contact Premier Food Safety if you believe this is incorrect.',
      },
      review: {
        title: 'Review access not enabled',
        message: 'Your SME profile does not have reviewer access. Contact Premier Food Safety if you believe this is incorrect.',
      },
    },

    bucket: {
      draft: 'Draft',
      inRevision: 'In revision',
      inReview: 'In review',
      accepted: 'Accepted',
      rejected: 'Rejected',
    },

    decision: {
      accept: 'Accept',
      revise: 'Revise',
      reject: 'Reject',
    },

    reviewType: {
      content: 'Content review',
      biasSensitivity: 'Bias & sensitivity review',
      editorial: 'Editorial review',
    },

    guidelineWarnings: {
      title: 'Item-writing guideline warnings',
      messages: {
        key_longest: 'The key is the longest option, which can give it away. Consider balancing option lengths (A7).',
        all_none_of_above: 'Avoid "all of the above" and "none of the above" as options (A3).',
        negative_not_capitalised: 'Write negative words in the stem in capitals (NOT, EXCEPT) so candidates do not miss them (S4).',
        numeric_order: 'List numeric options in ascending or descending order (style rules).',
        clue_word: 'A word from the stem appears only in the correct answer ({words}), which can cue it (A4).',
        option_length_spread: 'Option lengths differ by more than half. Similar lengths keep the correct answer from standing out (A7).',
        negative_stem: 'Use a negative stem only when necessary (S4).',
        second_person: 'Describe the food worker instead of addressing the test taker as "you".',
        subjective_qualifier: 'Use MOST, BEST or FIRST only when one answer is clearly correct, and write the word in capitals.',
        subjective_qualifier_capitalize: 'Use MOST, BEST or FIRST only when one answer is clearly correct, and write the word in capitals. Capitalize {words}.',
        blank_in_stem: 'Write the stem as a question or an incomplete sentence, not with a blank in the middle.',
        absolute_terms: 'Avoid absolute terms such as always, never or only in options (A6).',
        near_duplicate_options: 'Options {first} and {second} are nearly identical. Make each option clearly distinct (A5).',
        option_format_closed: 'The stem is a question, so start every option with a capital letter (style rules).',
        option_format_open: 'The stem is an incomplete sentence, so start every option with a lowercase letter and end it with a period (style rules).',
        long_sentence: 'Keep sentences short; aim for 15-20 words.',
        gendered_language: 'Use gender-neutral terms such as they, food worker or wait staff.',
        cultural_reference: 'Avoid holidays and cultural events; use a neutral setting such as a scheduled workplace event.',
      },
    },

    itemBrief: {
      heading: '{program} exam: item writing brief',
      hide: 'Hide brief',
      show: 'Show brief',
      dismiss: 'Dismiss',
    },

    reviewFeedback: {
      title: 'Reviewer feedback',
    },

    dashboard: {
      title: 'Dashboard',
      noRoles: 'Your SME profile has no writing or review access enabled yet. Contact Premier Food Safety to get started.',
      loading: 'Loading your dashboard',
      noAssignments: 'You have no writing assignment yet. Contact Premier Food Safety if you expected one.',
      assignmentClosed: 'Closed',
      dueOn: 'Due {date}',
      dueToday: 'Due today',
      daysLeft: 'one day left | {count} days left',
      daysOverdue: 'one day overdue | {count} days overdue',
      itemsWritten: '{written} of {quota} items written',
      progressLabel: 'Items written for {program}, {round}',
      writeItem: 'Write an item',
      nextUp: 'Next up: {domain}, {count} to go',
      quotaComplete: 'You have written every item in this assignment.',
      itemsHeading: 'Your items',
      itemsHint: 'Select a status to see those items.',
      tileLabel: '{status}: {count}. View these items.',
      waitingOnYou: 'Waiting on you',
      domainsHeading: 'Progress by domain',
      domainProgress: '{written} / {quota}',
      domainProgressLabel: '{domain}: {written} of {quota} items written',
      reviewQueueTitle: 'Review queue',
      itemsWaiting: 'one item waiting | {total} items waiting',
      startReviewing: 'Start reviewing',
    },

    reviewQueue: {
      empty: 'Nothing waiting for review right now.',
    },

    items: {
      title: 'My items',
      newItem: 'New item',
      noOpenAssignment: 'You have no open writing assignment right now, so there is nothing to write into.',
      filterBucket: 'Status',
      filterDomain: 'Domain',
      empty: 'No items match these filters.',
      untitled: 'Untitled draft',
      columns: {
        stem: 'Stem',
        domain: 'Domain',
        learningOutcome: 'Learning outcome',
        status: 'Status',
        version: 'Version',
        updatedAt: 'Last updated',
      },
    },

    form: {
      newTitle: 'New item',
      editTitle: 'Edit item',
      backToItems: 'Back to my items',
      statusVersion: '{status} · version {version}',
      readOnlyNotice: 'This item is no longer a draft and cannot be edited.',
      program: 'Program',
      domain: 'Domain',
      learningOutcome: 'Learning outcome',
      noOutcomeMapped: 'No learning outcome is mapped to this domain yet. This item cannot be saved until one is added.',
      stem: 'Stem',
      optionA: 'Option A',
      optionB: 'Option B',
      optionC: 'Option C',
      optionD: 'Option D',
      correctOption: 'Correct answer',
      rationale: 'Rationale',
      foodCodeReference: 'FDA Food Code reference',
      cognitiveLevel: 'Cognitive level',
      aiAssistance: 'AI assistance',
      aiAssistanceNote: 'AI assistance note',
      saveDraft: 'Save draft',
      saveAndAddAnother: 'Save and add another',
      submit: 'Submit',
      submitConfirmTitle: 'Submit this item?',
      submitConfirmMessage: 'Once submitted, this item moves into review and can no longer be edited unless a reviewer sends it back.',
      submitSuccess: 'Item submitted for review.',
      unsavedChangesTitle: 'Leave without saving?',
      unsavedChangesMessage: 'You have unsaved changes on this item. Leaving now will discard them.',
      conflict: {
        title: 'This item changed elsewhere',
        staleVersion: 'Someone else changed this item since it was loaded. Reload it to see the latest version before saving again.',
        notEditable: 'This item is no longer a draft, so it cannot be saved. Reload to see its current state.',
        reload: 'Reload',
      },
    },

    review: {
      title: 'Review queue',
      stageFilter: 'Stage',
      stageAll: 'All stages',
      stageContent: 'Content',
      stageBiasSensitivity: 'Bias & sensitivity',
      stageLabel: 'Stage: {stage}',
      summaryTotal: '{total} total',
      summaryContent: '{count} content',
      summaryBiasSensitivity: '{count} bias & sensitivity',
      remaining: '{count} remaining',
      empty: 'Nothing to review right now.',
      itemHeading: 'Item {id}',
      decision: 'Decision',
      notes: 'Notes',
      submitDecision: 'Submit decision',
      skip: 'Skip',
      loadingItem: 'Loading the next item',
      reservedUntil: 'Reserved for you until {time}',
      reservationExpired: 'Your reservation on this item has ended, so another reviewer may pick it up. You can still submit a decision, but it may be refused.',
      decisionNotSaved: 'Your decision was not saved. Your decision and notes are still here.',
      loadNext: 'Load next item',
      discardTitle: 'Discard your notes?',
      discardMessage: 'You have notes that have not been submitted. Continuing will discard them.',
      discardConfirm: 'Discard notes',
      announceDecisionSaved: 'Decision saved. Showing the next item to review.',
      announceDecisionSavedEmpty: 'Decision saved. Nothing left to review.',
      announceNextLoaded: 'Showing the next item to review.',
      announceUnavailable: 'This item is no longer available. Your decision and notes are still here.',
      correctAnswer: 'Correct answer',
      checklist: {
        show: 'Show checklist',
        hide: 'Hide checklist',
        guidance: 'Guidance only. Nothing here is saved.',
        contentTitle: 'Content review checklist',
        content1: 'One answer is clearly correct, and the rationale supports it.',
        content2: 'The item matches the tagged learning outcome.',
        content3: 'It is written at the food handler level (non-supervisory staff), not the manager level.',
        content4: 'The stem stands on its own, has no unnecessary detail and does not address the test taker as "you".',
        content5: 'MOST, BEST or FIRST is used only when one answer is impartially correct.',
        content6: 'Distractors are plausible to someone without full knowledge.',
        content7: 'Options are parallel, concise and similar in length.',
        content8: 'No absolute terms and no "all of the above" or "none of the above".',
        biasTitle: 'Bias and sensitivity checklist',
        bias1: 'Language is neutral and gender-neutral.',
        bias2: 'No holidays, customs, regional foods, sports or pop culture.',
        bias3: 'No brand or place names.',
        bias4: 'No emotional or graphic content.',
        bias5: 'Plain language at a food handler reading level.',
        bias6: 'Could a qualified candidate from any demographic group answer this correctly, based only on the intended knowledge or skill?',
        bias7: 'Is there anything that could cause offense, concern or discomfort unrelated to the job task?',
      },
    },
  },
};

export const smeWorkspaceI18nEs = {
  smeWorkspace: {
    userTypeReadable: 'Experto en la materia',

    mfa: {
      title: 'Verifique su identidad',
      intro: 'Por su seguridad, verifique el código que le enviamos antes de ver el contenido de la evaluación.',
      sendCode: 'Enviar código',
      resend: 'Reenviar código',
      resendIn: 'Podrá reenviarlo en {seconds} s',
      codeSentTo: 'Enviamos un código de 6 dígitos a {destination}.',
      rememberDevice: 'Este dispositivo se recordará durante {hours} horas.',
      codeLabel: 'Código de verificación',
      verify: 'Verificar',
      verifying: 'Verificando…',
      sending: 'Enviando…',
      errors: {
        codeInvalid: 'Ese código es incorrecto.',
        codeExpired: 'Ese código ha vencido. Solicite uno nuevo.',
        challengeNotFound: 'Ese código ya no es válido. Solicite uno nuevo.',
        challengeLimit: 'Demasiadas solicitudes de código. Espere antes de solicitar otro.',
        generic: 'Algo salió mal. Inténtelo de nuevo.',
      },
    },

    nav: {
      dashboard: 'Panel',
      myItems: 'Mis ítems',
      reviewQueue: 'Cola de revisión',
    },

    accessDenied: {
      title: 'Se requiere acceso de experto',
      message: 'Este espacio de trabajo requiere un perfil de experto activo con un contrato firmado y la '
        + 'capacitación completada. Comuníquese con Premier Food Safety para configurar su acceso.',
    },

    error: {
      title: 'Algo salió mal',
      generic: 'Algo salió mal. Inténtelo de nuevo.',
      retry: 'Intentar de nuevo',
    },

    state: {
      loading: 'Cargando',
    },

    roleRequired: {
      write: {
        title: 'Redacción de ítems no habilitada',
        message: 'Su perfil de experto no tiene acceso para redactar ítems. Comuníquese con Premier Food Safety si cree que es un error.',
      },
      review: {
        title: 'Acceso de revisión no habilitado',
        message: 'Su perfil de experto no tiene acceso de revisor. Comuníquese con Premier Food Safety si cree que es un error.',
      },
    },

    bucket: {
      draft: 'Borrador',
      inRevision: 'En revisión',
      inReview: 'En evaluación',
      accepted: 'Aceptado',
      rejected: 'Rechazado',
    },

    decision: {
      accept: 'Aceptar',
      revise: 'Revisar',
      reject: 'Rechazar',
    },

    reviewType: {
      content: 'Revisión de contenido',
      biasSensitivity: 'Revisión de sesgo y sensibilidad',
      editorial: 'Revisión editorial',
    },

    guidelineWarnings: {
      title: 'Advertencias de las pautas de redacción de ítems',
      messages: {
        key_longest: 'La clave es la opción más larga, lo que puede delatarla. Considere equilibrar la longitud de las opciones (A7).',
        all_none_of_above: 'Evite "todas las anteriores" y "ninguna de las anteriores" como opciones (A3).',
        negative_not_capitalised: 'Escriba en mayúsculas las palabras negativas del enunciado (NO, EXCEPTO) para que los candidatos no las pasen por alto (S4).',
        numeric_order: 'Ordene las opciones numéricas de forma ascendente o descendente (reglas de estilo).',
        clue_word: 'Una palabra del enunciado aparece solo en la respuesta correcta ({words}), lo que puede darle una pista al candidato (A4).',
        option_length_spread: 'La longitud de las opciones difiere en más de la mitad. Longitudes similares evitan que la respuesta correcta destaque (A7).',
        negative_stem: 'Use un enunciado negativo solo cuando sea necesario (S4).',
        second_person: 'Describa al manipulador de alimentos en lugar de dirigirse a quien presenta el examen como "usted".',
        subjective_qualifier: 'Use MÁS, MEJOR o PRIMERO solo cuando una respuesta sea claramente correcta, y escriba la palabra en mayúsculas.',
        subjective_qualifier_capitalize: 'Use MÁS, MEJOR o PRIMERO solo cuando una respuesta sea claramente correcta, y escriba la palabra en mayúsculas. Escriba en mayúsculas: {words}.',
        blank_in_stem: 'Redacte el enunciado como una pregunta o una oración incompleta, sin un espacio en blanco en medio.',
        absolute_terms: 'Evite términos absolutos como siempre, nunca o solo en las opciones (A6).',
        near_duplicate_options: 'Las opciones {first} y {second} son casi idénticas. Haga que cada opción sea claramente distinta (A5).',
        option_format_closed: 'El enunciado es una pregunta, por lo que cada opción debe comenzar con mayúscula (reglas de estilo).',
        option_format_open: 'El enunciado es una oración incompleta, por lo que cada opción debe comenzar con minúscula y terminar con un punto (reglas de estilo).',
        long_sentence: 'Mantenga las oraciones cortas; procure que tengan de 15 a 20 palabras.',
        gendered_language: 'Use términos neutros en cuanto al género, como "la persona", "el manipulador de alimentos" o "el personal de servicio".',
        cultural_reference: 'Evite días festivos y eventos culturales; use un entorno neutral, como un evento programado en el lugar de trabajo.',
      },
    },

    itemBrief: {
      heading: 'Examen de {program}: guía de redacción de ítems',
      hide: 'Ocultar guía',
      show: 'Mostrar guía',
      dismiss: 'Cerrar',
    },

    reviewFeedback: {
      title: 'Comentarios del revisor',
    },

    dashboard: {
      title: 'Panel',
      noRoles: 'Su perfil de experto aún no tiene acceso de redacción ni de revisión. Comuníquese con Premier Food Safety para comenzar.',
      loading: 'Cargando su panel',
      noAssignments: 'Aún no tiene una asignación de redacción. Comuníquese con Premier Food Safety si esperaba una.',
      assignmentClosed: 'Cerrada',
      dueOn: 'Vence el {date}',
      dueToday: 'Vence hoy',
      daysLeft: 'queda un día | quedan {count} días',
      daysOverdue: 'un día de retraso | {count} días de retraso',
      itemsWritten: '{written} de {quota} ítems redactados',
      progressLabel: 'Ítems redactados para {program}, {round}',
      writeItem: 'Redactar un ítem',
      nextUp: 'Siguiente: {domain}, faltan {count}',
      quotaComplete: 'Ha redactado todos los ítems de esta asignación.',
      itemsHeading: 'Sus ítems',
      itemsHint: 'Seleccione un estado para ver esos ítems.',
      tileLabel: '{status}: {count}. Ver estos ítems.',
      waitingOnYou: 'Esperando su acción',
      domainsHeading: 'Avance por dominio',
      domainProgress: '{written} / {quota}',
      domainProgressLabel: '{domain}: {written} de {quota} ítems redactados',
      reviewQueueTitle: 'Cola de revisión',
      itemsWaiting: 'hay un ítem en espera | hay {total} ítems en espera',
      startReviewing: 'Comenzar a revisar',
    },

    reviewQueue: {
      empty: 'No hay nada en espera de revisión por ahora.',
    },

    items: {
      title: 'Mis ítems',
      newItem: 'Nuevo ítem',
      noOpenAssignment: 'No tiene una asignación de redacción abierta por ahora, así que no hay dónde redactar.',
      filterBucket: 'Estado',
      filterDomain: 'Dominio',
      empty: 'Ningún ítem coincide con estos filtros.',
      untitled: 'Borrador sin título',
      columns: {
        stem: 'Enunciado',
        domain: 'Dominio',
        learningOutcome: 'Resultado de aprendizaje',
        status: 'Estado',
        version: 'Versión',
        updatedAt: 'Última actualización',
      },
    },

    form: {
      newTitle: 'Nuevo ítem',
      editTitle: 'Editar ítem',
      backToItems: 'Volver a mis ítems',
      statusVersion: '{status} · versión {version}',
      readOnlyNotice: 'Este ítem ya no es un borrador y no se puede editar.',
      program: 'Programa',
      domain: 'Dominio',
      learningOutcome: 'Resultado de aprendizaje',
      noOutcomeMapped: 'Aún no hay un resultado de aprendizaje asignado a este dominio. Este ítem no se puede guardar hasta que se agregue uno.',
      stem: 'Enunciado',
      optionA: 'Opción A',
      optionB: 'Opción B',
      optionC: 'Opción C',
      optionD: 'Opción D',
      correctOption: 'Respuesta correcta',
      rationale: 'Justificación',
      foodCodeReference: 'Referencia del Código de Alimentos de la FDA',
      cognitiveLevel: 'Nivel cognitivo',
      aiAssistance: 'Asistencia de IA',
      aiAssistanceNote: 'Nota sobre la asistencia de IA',
      saveDraft: 'Guardar borrador',
      saveAndAddAnother: 'Guardar y agregar otro',
      submit: 'Enviar',
      submitConfirmTitle: '¿Enviar este ítem?',
      submitConfirmMessage: 'Una vez enviado, este ítem pasa a revisión y ya no se puede editar a menos que un revisor lo devuelva.',
      submitSuccess: 'Ítem enviado a revisión.',
      unsavedChangesTitle: '¿Salir sin guardar?',
      unsavedChangesMessage: 'Tiene cambios sin guardar en este ítem. Si sale ahora, se perderán.',
      conflict: {
        title: 'Este ítem cambió en otro lugar',
        staleVersion: 'Otra persona cambió este ítem después de que usted lo cargó. Vuelva a cargarlo para ver la versión más reciente antes de guardar de nuevo.',
        notEditable: 'Este ítem ya no es un borrador, por lo que no se puede guardar. Vuelva a cargarlo para ver su estado actual.',
        reload: 'Volver a cargar',
      },
    },

    review: {
      title: 'Cola de revisión',
      stageFilter: 'Etapa',
      stageAll: 'Todas las etapas',
      stageContent: 'Contenido',
      stageBiasSensitivity: 'Sesgo y sensibilidad',
      stageLabel: 'Etapa: {stage}',
      summaryTotal: '{total} en total',
      summaryContent: '{count} de contenido',
      summaryBiasSensitivity: '{count} de sesgo y sensibilidad',
      remaining: 'Faltan {count}',
      empty: 'No hay nada para revisar por ahora.',
      itemHeading: 'Ítem {id}',
      decision: 'Decisión',
      notes: 'Notas',
      submitDecision: 'Enviar decisión',
      skip: 'Omitir',
      loadingItem: 'Cargando el siguiente ítem',
      reservedUntil: 'Reservado para usted hasta las {time}',
      reservationExpired: 'Su reserva de este ítem terminó, así que otro revisor podría tomarlo. Aún puede enviar una decisión, pero podría ser rechazada.',
      decisionNotSaved: 'Su decisión no se guardó. Su decisión y sus notas siguen aquí.',
      loadNext: 'Cargar el siguiente ítem',
      discardTitle: '¿Descartar sus notas?',
      discardMessage: 'Tiene notas que no se han enviado. Si continúa, se descartarán.',
      discardConfirm: 'Descartar notas',
      announceDecisionSaved: 'Decisión guardada. Se muestra el siguiente ítem para revisar.',
      announceDecisionSavedEmpty: 'Decisión guardada. No queda nada por revisar.',
      announceNextLoaded: 'Se muestra el siguiente ítem para revisar.',
      announceUnavailable: 'Este ítem ya no está disponible. Su decisión y sus notas siguen aquí.',
      correctAnswer: 'Respuesta correcta',
      checklist: {
        show: 'Mostrar lista de verificación',
        hide: 'Ocultar lista de verificación',
        guidance: 'Solo orientación. Nada de lo que aparece aquí se guarda.',
        contentTitle: 'Lista de verificación de la revisión de contenido',
        content1: 'Una respuesta es claramente correcta y la justificación la respalda.',
        content2: 'El ítem coincide con el resultado de aprendizaje indicado.',
        content3: 'Está redactado para el nivel de manipulador de alimentos (personal no supervisor), no para el nivel de gerente.',
        content4: 'El enunciado se entiende por sí solo, no tiene detalles innecesarios y no se dirige a quien presenta el examen como "usted".',
        content5: 'MÁS, MEJOR o PRIMERO se usa solo cuando una respuesta es correcta de forma imparcial.',
        content6: 'Los distractores son plausibles para quien no domina el tema por completo.',
        content7: 'Las opciones son paralelas, concisas y de longitud similar.',
        content8: 'Sin términos absolutos ni "todas las anteriores" o "ninguna de las anteriores".',
        biasTitle: 'Lista de verificación de sesgo y sensibilidad',
        bias1: 'El lenguaje es neutral y neutro en cuanto al género.',
        bias2: 'Sin días festivos, costumbres, comidas regionales, deportes ni cultura popular.',
        bias3: 'Sin marcas comerciales ni nombres de lugares.',
        bias4: 'Sin contenido emocional ni gráfico.',
        bias5: 'Lenguaje sencillo, al nivel de lectura de un manipulador de alimentos.',
        bias6: '¿Podría un candidato calificado de cualquier grupo demográfico responder correctamente, basándose solo en el conocimiento o la habilidad que se evalúa?',
        bias7: '¿Hay algo que pueda causar ofensa, preocupación o incomodidad sin relación con la tarea del puesto?',
      },
    },
  },
};
